import { shiftMonths } from '../format/dates'
import type { LedgerEntry, LedgerUnit, VoucherStatus, VoucherValidFor } from '../types/vouchers'

/**
 * Grootboek voor cadeaubonnen en klanttegoed (BEHEER-PLAN §3.3–3.4) — één set regels voor het
 * beheer, de webshop, de demo en de echte server.
 *
 * - Er komen alleen regels bij (uitgegeven, ingewisseld, vervallen, correctie); nooit aanpassen of weghalen.
 * - Het saldo is de som per eenheid: centen en behandelingen tellen nooit samen.
 * - Een saldo zakt nooit onder 0.
 * - Een bon is te gebruiken tot en met `validUntil`; daarna vervalt wat er nog op staat.
 */

/** Saldo in één eenheid. */
export const balance = (ledger: LedgerEntry[], unit: LedgerUnit): number =>
  ledger.reduce((s, e) => (e.unit === unit ? s + e.amount : s), 0)

/** Saldo per eenheid. */
export const balances = (ledger: LedgerEntry[]) => ({ cents: balance(ledger, 'cents'), treatment: balance(ledger, 'treatment') })

/** Waarom deze regel er niet bij kan, of `null`. */
export function entryProblem(ledger: LedgerEntry[], entry: Pick<LedgerEntry, 'unit' | 'amount' | 'kind' | 'note'>): string | null {
  if (!Number.isInteger(entry.amount) || entry.amount === 0) return 'Vul een bedrag of aantal in dat niet 0 is.'
  if (entry.kind === 'correctie' && !entry.note?.trim()) return 'Vul een reden in. Die staat in het grootboek.'
  if (balance(ledger, entry.unit) + entry.amount < 0) return 'Zoveel staat er niet op; het saldo kan niet onder 0.'
  return null
}

/** Na de einddatum (de dag zelf telt nog mee). */
export const isExpired = (validUntil: string, today: string) => today > validUntil

/** Verlopen wint; dan geblokkeerd; anders leeg bij 0, anders actief. */
export function voucherStatus(v: { balanceCents: number; validUntil: string; blocked?: unknown }, today: string): VoucherStatus {
  if (isExpired(v.validUntil, today)) return 'verlopen'
  if (v.blocked) return 'geblokkeerd'
  return v.balanceCents > 0 ? 'actief' : 'leeg'
}

/**
 * De vervalregel voor een verlopen bon met nog saldo, of `null` (niet verlopen, leeg, of al vervallen).
 * Valt op de einddatum, aan het eind van de dag.
 */
export function expiryEntry(ledger: LedgerEntry[], validUntil: string, today: string, id: string): LedgerEntry | null {
  const left = balance(ledger, 'cents')
  if (!isExpired(validUntil, today) || left <= 0 || ledger.some((e) => e.kind === 'vervallen')) return null
  return { id, at: `${validUntil}T23:59`, kind: 'vervallen', unit: 'cents', amount: -left }
}

export type VoucherProblemCode = 'voucher_expired' | 'voucher_blocked' | 'voucher_empty' | 'voucher_treatments_only'

/**
 * Waarom deze bon hier niet te gebruiken is, of `null`. De webshop verkoopt alleen producten:
 * daar geldt alleen een bon "ook voor producten" (`all`).
 */
export function voucherProblem(
  v: { balanceCents: number; validUntil: string; validFor: VoucherValidFor; blocked?: unknown },
  today: string,
  use: 'products' | 'treatments',
): VoucherProblemCode | null {
  if (isExpired(v.validUntil, today)) return 'voucher_expired'
  if (v.blocked) return 'voucher_blocked'
  if (v.balanceCents <= 0) return 'voucher_empty'
  if (use === 'products' && v.validFor === 'treatments') return 'voucher_treatments_only'
  return null
}

/** Hoeveel van de bon er bij deze bestelling af gaat: nooit meer dan er te betalen is. */
export const redeemCents = (balanceCents: number, payableCents: number) => Math.max(0, Math.min(balanceCents, payableCents))

/** Tot wanneer een nieuwe bon geldt: zoveel maanden na uitgifte. */
export const validUntilFor = (issuedOn: string, months: number) => shiftMonths(issuedOn, months)

// ——— Codes ———

/** Zonder 0/O, 1/I/L: niet te verwarren bij overtypen van een geprinte bon. */
const ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'
const GROUPS = 3
const GROUP = 4

/**
 * Een nieuwe code, bijv. "BB-7K3Q-M9TX-4HPA" (31¹² mogelijkheden; met de rate limit op
 * POST /vouchers/check niet te raden). `random` geeft getallen in [0, 1); de server gebruikt
 * een cryptografisch veilige bron.
 */
export function voucherCode(random: () => number): string {
  const group = () => Array.from({ length: GROUP }, () => ALPHABET[Math.floor(random() * ALPHABET.length)]).join('')
  return ['BB', ...Array.from({ length: GROUPS }, group)].join('-')
}

/**
 * Wat iemand intypt → de nette vorm, of `null` als het geen geldige code kan zijn.
 * Hoofd- of kleine letters, spaties en streepjes maken niet uit; "BB" vooraan mag ontbreken.
 */
export function normalizeVoucherCode(input: string): string | null {
  let raw = input.toUpperCase().replace(/[^A-Z0-9]/g, '')
  if (raw.startsWith('BB') && raw.length === 2 + GROUPS * GROUP) raw = raw.slice(2)
  if (raw.length !== GROUPS * GROUP || [...raw].some((c) => !ALPHABET.includes(c))) return null
  const groups = raw.match(new RegExp(`.{${GROUP}}`, 'g')) ?? []
  return ['BB', ...groups].join('-')
}

/** De laatste vier tekens, voor lijsten en logs. */
export const codeLast4 = (code: string) => code.replace(/[^A-Z0-9]/gi, '').slice(-4).toUpperCase()
